import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

export interface UserCertification {
  id: string;
  user_id: string;
  name: string;
  issuer?: string;
  date?: string;
  description?: string;
  url?: string;
  created_at: string;
  updated_at: string;
}

export const useUserCertifications = (userId?: string) => {
  const { user } = useUser();
  const [certifications, setCertifications] = useState<UserCertification[]>([]);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  const fetchCertifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_certifications')
        .select('*')
        .eq('user_id', targetUserId as any)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching certifications:', error);
        return;
      }

      setCertifications((data as any) || []);
    } catch (error) {
      console.error('Error fetching certifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!targetUserId) {
      setCertifications([]);
      setLoading(false);
      return;
    }

    fetchCertifications();
    
    // Listen for profile data updates
    const handleProfileUpdate = () => {
      if (targetUserId) {
        fetchCertifications();
      }
    };
    
    window.addEventListener('profileDataUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profileDataUpdated', handleProfileUpdate);
    };
  }, [targetUserId]);

  const addCertification = async (certification: Omit<UserCertification, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_certifications')
        .insert({ ...certification, user_id: user.id } as any)
        .select()
        .single();

      if (error) throw error;

      setCertifications(prev => [(data as any), ...prev]);
      return data;
    } catch (error) {
      console.error('Error adding certification:', error);
      throw error;
    }
  };

  const updateCertification = async (id: string, updates: Partial<UserCertification>) => {
    try {
      const { data, error } = await supabase
        .from('user_certifications')
        .update(updates as any)
        .eq('id', id as any)
        .select()
        .single();

      if (error) throw error;

      setCertifications(prev => prev.map(c => c.id === id ? (data as any) : c));
      return data;
    } catch (error) {
      console.error('Error updating certification:', error);
      throw error;
    }
  };

  const deleteCertification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_certifications')
        .delete()
        .eq('id', id as any);

      if (error) throw error;

      setCertifications(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting certification:', error);
      throw error;
    }
  };

  return {
    certifications,
    loading,
    addCertification,
    updateCertification,
    deleteCertification,
    refetchCertifications: fetchCertifications
  };
};